import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Edit, Trash, Users, Briefcase, MessageSquare, History } from "lucide-react";
import { useCompanyMatchesList } from "@/hooks/companies/useCompanyMatchesList";
import { useCompanyJobsList } from "@/hooks/companies/useCompanyJobsList";
import { useCompanyContactsList } from "@/hooks/companies/useCompanyContactsList";
import { useCompanyDetail } from "@/hooks/companies/useCompanyDetail";
import { AddContactForm } from "@/components/companies/AddContactForm";
import { CompanyComments } from "@/components/companies/CompanyComments";
import { CompanyHeader } from "@/components/companies/CompanyHeader";
import { CompanyMatchesTab } from "@/components/companies/CompanyMatchesTab";
import { CompanyOffersTab } from "@/components/companies/CompanyOffersTab";
import { CompanyContactsTab } from "@/components/companies/CompanyContactsTab";
import { CompanyDetailSkeleton } from "@/components/companies/CompanyDetailSkeleton";
import { CompanyNotFound } from "@/components/companies/CompanyNotFound";
import { CompanyHistoryTab } from "@/components/companies/CompanyHistoryTab";
import { useCompanies } from "@/hooks/useCompanies";
import { useOrganizationManagement } from "@/hooks/useOrganizationManagement";
import { EditCompanyModal, CompanyFormValues } from "@/components/companies/EditCompanyModal";
import { useTranslation } from "react-i18next";

interface Job {
  id: string;
  title: string;
  company_id: string;
  company_name?: string;
  status?: string;
  created_at: string;
  skills?: string[];
  description?: string;
  [key: string]: any;
}

const CompanyDetail = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [addContactModalOpen, setAddContactModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("matches");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  const { company, loading, refreshCompany } = useCompanyDetail(id);
  const { fetchCompanies } = useCompanies();
  const { handleEditCompany } = useOrganizationManagement({ fetchCompanies });
  
  const { 
    matches, 
    matchesLoading, 
    searchTerm: matchesSearchTerm, 
    setSearchTerm: setMatchesSearchTerm,
    viewMode: matchesViewMode,
    setViewMode: setMatchesViewMode
  } = useCompanyMatchesList(id);
  
  const {
    jobs,
    jobsLoading,
    searchTerm: jobsSearchTerm,
    setSearchTerm: setJobsSearchTerm,
    viewMode: jobsViewMode,
    setViewMode: setJobsViewMode
  } = useCompanyJobsList(id);
  
  const {
    contacts,
    contactsLoading,
    searchTerm: contactsSearchTerm,
    setSearchTerm: setContactsSearchTerm,
    viewMode: contactsViewMode,
    setViewMode: setContactsViewMode,
    refreshContacts
  } = useCompanyContactsList(id);
  
  if (loading) {
    return <CompanyDetailSkeleton />;
  }
  
  if (!company) {
    return <CompanyNotFound />;
  }
  
  const handleAddContact = async (contactData: any) => {
    setAddContactModalOpen(false);
    refreshContacts();
  };

  const handleEditClick = () => {
    setIsEditModalOpen(true);
  };
  
  const handleEditSubmit = async (formData: CompanyFormValues) => {
    if (!id || !company) return false;
    
    try {
      // Ensure all required fields are present
      const companyData = {
        id: id,
        name: formData.name,
        street: formData.street,
        postal_code: formData.postal_code,
        city: formData.city,
        country: formData.country,
        assignee: formData.assignee,
        notes: formData.notes,
        vat_number: formData.vat_number,
        logo_url: formData.logo_url
      };
      
      const success = await handleEditCompany(companyData);
      
      if (success) {
        refreshCompany();
      }
      
      return success;
    } catch (error) {
      console.error("Error editing company:", error);
      return false;
    }
  };
  
  return (
    <div>
      <PageHeader 
        title={t("companies.detail.title")}
        actions={
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              className="flex items-center gap-2"
              onClick={handleEditClick}
            >
              <Edit className="h-4 w-4" />
              <span>{t("common.edit")}</span>
            </Button>
            <Button variant="outline" className="flex items-center gap-2 text-red-600 hover:text-red-700">
              <Trash className="h-4 w-4" />
              <span>{t("common.delete")}</span>
            </Button>
          </div>
        }
      />
      
      <div className="mb-6">
        <Link to="/companies" className="text-digger-blue hover:underline flex items-center">
          <ArrowLeft className="h-4 w-4 mr-1" />
          {t("companies.detail.backToCompanies")}
        </Link>
      </div>
      
      <CompanyHeader company={company} refreshCompany={refreshCompany} />
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full mb-4">
          <TabsTrigger value="matches" className="flex-1 flex items-center justify-center gap-2">
            <Users className="h-4 w-4" />
            <span>{t("companies.detail.tabs.matches")}</span>
          </TabsTrigger>
          <TabsTrigger value="offers" className="flex-1 flex items-center justify-center gap-2">
            <Briefcase className="h-4 w-4" />
            <span>{t("companies.detail.tabs.offers")}</span>
          </TabsTrigger>
          <TabsTrigger value="contacts" className="flex-1 flex items-center justify-center gap-2">
            <Users className="h-4 w-4" />
            <span>{t("companies.detail.tabs.contacts")}</span>
          </TabsTrigger>
          <TabsTrigger value="comments" className="flex-1 flex items-center justify-center gap-2">
            <MessageSquare className="h-4 w-4" />
            <span>{t("companies.detail.tabs.comments")}</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="flex-1 flex items-center justify-center gap-2">
            <History className="h-4 w-4" />
            <span>{t("companies.detail.tabs.history")}</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="matches" className="space-y-4">
          <CompanyMatchesTab
            matches={matches}
            loading={matchesLoading}
            searchTerm={matchesSearchTerm}
            setSearchTerm={setMatchesSearchTerm}
            viewMode={matchesViewMode}
            setViewMode={setMatchesViewMode}
          />
        </TabsContent>
        
        <TabsContent value="offers" className="space-y-4">
          <CompanyOffersTab
            jobs={jobs as Job[]}
            loading={jobsLoading}
            searchTerm={jobsSearchTerm}
            setSearchTerm={setJobsSearchTerm}
            viewMode={jobsViewMode}
            setViewMode={setJobsViewMode}
            companyId={id || ''}
          />
        </TabsContent>
        
        <TabsContent value="contacts" className="space-y-4">
          <CompanyContactsTab
            contacts={contacts}
            loading={contactsLoading}
            searchTerm={contactsSearchTerm}
            setSearchTerm={setContactsSearchTerm}
            viewMode={contactsViewMode}
            setViewMode={setContactsViewMode}
            companyId={id || ''}
            onAddContact={() => setAddContactModalOpen(true)}
            refreshContacts={refreshContacts}
          />
        </TabsContent>
        
        <TabsContent value="comments" className="space-y-4">
          {id && <CompanyComments companyId={id} />}
        </TabsContent>
        
        <TabsContent value="history" className="space-y-4">
          {id && <CompanyHistoryTab companyId={id} />}
        </TabsContent>
      </Tabs>
      
      <AddContactForm
        isOpen={addContactModalOpen}
        onClose={() => setAddContactModalOpen(false)}
        onSubmit={handleAddContact}
        companyId={id || ''}
      />

      <EditCompanyModal
        company={company}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSubmit={handleEditSubmit}
      />
    </div>
  );
};

export default CompanyDetail;
